/*
 * Gazelle Glam cart drawer behaviour.
 * Works with sections/gg-cart-drawer.liquid and its snippets.
 *
 *  - Any [data-gg-cart-open] element (or a plain link to /cart) opens the drawer.
 *  - Quantity, remove and "add suggestion" update the cart with the Ajax API and
 *    re-render the drawer with the Section Rendering API (no page reload).
 *  - window.ggCart.open() / .close() / .refresh() / .add(variantId, qty)
 *  - Dispatch document event "gg:cart:added" after adding a product elsewhere and
 *    the drawer refreshes and opens.
 *
 *  Smoothness passes in this version:
 *   - render() skips the DOM swap entirely when fetched HTML is unchanged
 *     (kills the flicker-on-every-open issue).
 *   - render() crossfades the panel content when it DOES change, instead of
 *     an instant innerHTML replace (which forces every <img> to re-decode
 *     and repaint all at once).
 *   - Quantity +/- gives instant optimistic feedback (the number updates
 *     immediately, before the network round trip resolves) so taps never
 *     feel laggy, and the buttons are guarded against double-fires.
 *   - Removing a line fades and collapses it in place first, then performs
 *     the actual removal, instead of the row just vanishing abruptly.
 */
(function () {
  'use strict';
  if (window.ggCart) return;

  var ROOT = '[data-gg-cart]';
  var queue = Promise.resolve();
  var lastFocus = null;
  var closeTimer = null;
  var errorTimer = null;
  var FADE_MS = 140;

  function root() { return document.querySelector(ROOT); }
  function panel() { var r = root(); return r && r.querySelector('.gg-cart__panel'); }
  function base() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }
  function sectionId() {
    var r = root();
    var wrap = r && r.closest('[id^="shopify-section-"]');
    return wrap ? wrap.id.replace('shopify-section-', '') : 'gg-cart-drawer';
  }
  function isOpen() { var r = root(); return !!r && r.classList.contains('is-open'); }
  function plainClick(e) {
    return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
  }
  function isCartLink(a) {
    try {
      var u = new URL(a.href, window.location.href);
      return u.origin === window.location.origin && /(^|\/)cart\/?$/.test(u.pathname);
    } catch (err) { return false; }
  }

  /* ---------- network ---------- */
  function post(path, body) {
    return fetch(base() + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok || data.status) throw data;
        return data;
      });
    });
  }

  function setBusy(on) {
    var r = root();
    if (r) r.classList.toggle('is-busy', on);
  }
  function enqueue(fn) {
    queue = queue.then(function () { setBusy(true); return fn(); })
      .catch(function () { /* handled inside fn */ })
      .then(function () { setBusy(false); });
    return queue;
  }

  /* ---------- rendering ---------- */
  function rememberFocus() {
    var a = document.activeElement, p = panel();
    if (!a || !p || !p.contains(a)) return null;
    var line = a.closest('[data-gg-line]');
    return {
      key: line ? line.getAttribute('data-key') : null,
      qty: a.getAttribute('data-gg-qty'),
      remove: a.hasAttribute('data-gg-remove'),
      close: a.hasAttribute('data-gg-cart-close')
    };
  }
  function restoreFocus(mem) {
    var p = panel();
    if (!p || !isOpen()) return;
    var target = null;
    if (mem && mem.key) {
      var line = p.querySelector('[data-gg-line][data-key="' + mem.key.replace(/"/g, '\\"') + '"]');
      if (line) {
        if (mem.qty) target = line.querySelector('[data-gg-qty="' + mem.qty + '"]');
        else if (mem.remove) target = line.querySelector('[data-gg-remove]');
      }
    }
    if (!target && mem && mem.close) target = p.querySelector('[data-gg-cart-dismiss]');
    if (!target) {
      p.setAttribute('tabindex', '-1');
      target = p;
    }
    try { target.focus({ preventScroll: true }); } catch (err) { /* ignore */ }
  }

  /* Re-run the BUCKS currency converter on freshly rendered drawer HTML */
  function currencyRefresh() {
    ['BUCKSCC', 'BUCKS', 'buckscc', 'Bucks'].forEach(function (k) {
      var o = window[k]; if (!o) return;
      ['reload', 'refresh', 'convertAll'].forEach(function (m) {
        if (typeof o[m] === 'function') { try { o[m](); } catch (err) { /* ignore */ } }
      });
    });
    document.dispatchEvent(new CustomEvent('gg:price-updated'));
    /* Re-fire the converter's own currency picker so it converts the freshly
       rendered drawer exactly like a manual switch would. */
    try {
      document.querySelectorAll(
        'select[name*="currenc" i], select[id*="currenc" i], select[class*="currenc" i], select[class*="bucks" i], select[id*="bucks" i], [data-currency-select]'
      ).forEach(function (sel) {
        if (sel.closest('[data-gg-cart]')) return;
        sel.dispatchEvent(new Event('change', { bubbles: true }));
      });
    } catch (err) { /* ignore */ }
  }

  function updateCount() {
    var p = panel();
    var c = p && p.querySelector('[data-gg-count]');
    if (!c) return;
    var n = c.textContent.trim();
    document.querySelectorAll('[data-gg-cart-count]').forEach(function (el) {
      el.textContent = n;
      el.hidden = n === '0';
    });
  }

  /* Structure signature that ignores prices. The currency converter rewrites
     price text in the live drawer, so a plain innerHTML compare always saw
     "changed" on open and swapped the converted prices back to AED. Comparing
     with prices (and converter wrappers) stripped keeps the converted DOM when
     only the currency differs. */
  function sig(node) {
    var c = node.cloneNode(true);
    c.querySelectorAll('.gg-money-view').forEach(function (n) { n.remove(); });
    c.querySelectorAll('.money').forEach(function (n) { n.replaceWith(document.createTextNode(n.textContent)); });
    c.querySelectorAll('[class*="gg-cart__panel-inner--swapping"],[data-gg-error]').forEach(function (n) { n.removeAttribute('style'); });
    return c.innerHTML
      .replace(/[\u2212\-+]?\s*[\d,]+\.\d{2}/g, '\u00a7')
      .replace(/AED|SAR|SR\b|USD|Dhs?\.?|\u0631\.\u0633/gi, '')
      .replace(/\s+/g, ' ');
  }

  function render(html) {
    var p = panel();
    if (!p || !html) return;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var fresh = doc.querySelector('.gg-cart__panel');
    if (!fresh) return;

    /* Skip the swap entirely when nothing actually changed — the common
       case on a plain open(). Avoids recreating every <img> for no reason. */
    if (sig(fresh) === sig(p)) {
      updateCount();
      currencyRefresh();
      return;
    }

    var lines = p.querySelector('[data-gg-lines]');
    var top = lines ? lines.scrollTop : 0;
    var mem = rememberFocus();

    function applySwap() {
      p.innerHTML = fresh.innerHTML;
      var newLines = p.querySelector('[data-gg-lines]');
      if (newLines) newLines.scrollTop = top;
      updateCount();
      restoreFocus(mem);
      currencyRefresh();
      setTimeout(currencyRefresh, 250);
      /* fade content back in on the next frame */
      p.classList.add('gg-cart__panel--swapping');
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          p.classList.remove('gg-cart__panel--swapping');
        });
      });
    }

    /* Crossfade: fade the current content out, THEN swap the DOM, then
       fade the new content in. Feels like a soft update instead of a hard
       cut, and gives image decode time to happen while opacity is low. */
    p.classList.add('gg-cart__panel--swapping');
    setTimeout(applySwap, FADE_MS);
  }

  function showError(err) {
    var p = panel();
    var box = p && p.querySelector('[data-gg-error]');
    var msg = (err && (err.description || err.message)) || 'Something went wrong. Please try again.';
    if (!box) return;
    box.textContent = msg;
    box.hidden = false;
    clearTimeout(errorTimer);
    errorTimer = setTimeout(function () { box.hidden = true; }, 5000);
  }

  function refresh() {
    var id = sectionId();
    return fetch(base() + '?sections=' + encodeURIComponent(id) + '&t=' + Date.now(), {
      headers: { Accept: 'application/json' }, cache: 'no-store'
    })
      .then(function (r) { return r.json(); })
      .then(function (data) { render(data[id]); })
      .catch(function () { /* keep what is on screen */ });
  }

  function applyCart(data) {
    var id = sectionId();
    if (data && data.sections && data.sections[id]) {
      render(data.sections[id]);
      return Promise.resolve();
    }
    return refresh();
  }

  /* ---------- cart actions ---------- */
  function change(key, quantity) {
    return enqueue(function () {
      return post('cart/change.js', {
        id: key, quantity: quantity,
        sections: sectionId(), sections_url: window.location.pathname
      }).then(applyCart, function (err) { showError(err); return refresh(); });
    });
  }

  function add(variantId, quantity) {
    return enqueue(function () {
      return post('cart/add.js', {
        items: [{ id: Number(variantId), quantity: quantity || 1 }],
        sections: sectionId(), sections_url: window.location.pathname
      }).then(applyCart, function (err) { showError(err); return refresh(); });
    });
  }

  /* Adds several line items in a single request — used by "Frequently
     Bought Together" style features that add the current product plus
     one or more companion products together. */
  function addItems(items) {
    return enqueue(function () {
      return post('cart/add.js', {
        items: items,
        sections: sectionId(), sections_url: window.location.pathname
      }).then(applyCart, function (err) { showError(err); return refresh(); });
    });
  }

  /* Accepts a FormData payload (from an actual product/variant <form>) rather
     than a bare variant id — used to intercept ordinary add-to-cart forms
     (product cards, PDP, kit builder, etc.) so their default full-page submit
     never happens; they open the drawer instead, everywhere on the site. */
  function addForm(formData) {
    formData.append('sections', sectionId());
    formData.append('sections_url', window.location.pathname);
    return enqueue(function () {
      return fetch(base() + 'cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData
      })
        .then(function (res) {
          return res.json().then(function (data) {
            if (!res.ok || data.status) throw data;
            return data;
          });
        })
        .then(applyCart, function (err) { showError(err); return refresh(); });
    });
  }

  /* ---------- open / close ---------- */
  function open(skipRefresh) {
    var r = root();
    if (!r) return;
    clearTimeout(closeTimer);
    if (!isOpen()) lastFocus = document.activeElement;
    r.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    void r.offsetWidth;
    r.classList.add('is-open');
    setTimeout(currencyRefresh, 50);
    var p = panel();
    var btn = p && p.querySelector('[data-gg-cart-dismiss]');
    setTimeout(function () {
      if (!isOpen()) return;
      try { (btn || p).focus({ preventScroll: true }); } catch (err) { /* ignore */ }
    }, 60);
    if (!skipRefresh) refresh();
  }

  function close() {
    var r = root();
    if (!r || !isOpen()) return;
    r.classList.remove('is-open');
    document.documentElement.style.overflow = '';
    closeTimer = setTimeout(function () { if (!isOpen()) r.hidden = true; }, 360);
    if (lastFocus && lastFocus.focus) {
      try { lastFocus.focus({ preventScroll: true }); } catch (err) { /* ignore */ }
    }
  }

  /* ---------- events ---------- */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;

    var r = root();
    var inside = r && r.contains(t);

    if (!inside) {
      var opener = t.closest('[data-gg-cart-open]');
      if (!opener) {
        var a = t.closest('a[href]');
        if (a && isCartLink(a)) opener = a;
      }
      if (opener && plainClick(e)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        open();
      }
      return;
    }

    if (t.closest('[data-gg-cart-close]')) { close(); return; }

    /* Some stores run a third-party checkout app (e.g. GulfCart) that
       intercepts this button and opens its own login/OTP popup on top of
       the page — without knowing to close OUR drawer first. Rather than
       depend on that app's own script to know about us, we close
       immediately on click ourselves, so by the time any such popup
       appears our drawer is already out of the way. This runs before the
       click is left to bubble/continue to whatever else is listening for
       it (the actual add/change/submit still proceeds normally). */
    if (t.closest('[data-gg-checkout]')) { close(); }

    var qtyBtn = t.closest('[data-gg-qty]');
    if (qtyBtn) {
      if (qtyBtn.disabled) return;
      var line = qtyBtn.closest('[data-gg-line]');
      if (!line) return;
      var delta = Number(qtyBtn.getAttribute('data-gg-qty'));
      var current = Number(line.getAttribute('data-qty'));
      var next = Math.max(0, current + delta);

      /* Optimistic update: reflect the new number immediately so the tap
         feels instant, rather than waiting on the network round trip. The
         real render() from the server response reconciles shortly after
         (and corrects it if the request actually failed or hit a stock
         limit). Guard both buttons on this line so a rapid double-tap
         can't get ahead of itself before the optimistic state is applied. */
      var qtyLabel = line.querySelector('[aria-live="polite"]');
      if (qtyLabel) qtyLabel.textContent = next;
      line.setAttribute('data-qty', next);
      line.querySelectorAll('[data-gg-qty]').forEach(function (b) { b.disabled = true; });
      if (next === 0) line.classList.add('gg-cart-line--removing');

      change(line.getAttribute('data-key'), next).then(function () {
        /* If the panel didn't end up re-rendering (identical-content skip
           for some edge case), still re-enable the buttons. render() will
           otherwise naturally replace these nodes with fresh, enabled ones. */
        var stillThere = document.querySelector(
          '[data-gg-line][data-key="' + line.getAttribute('data-key').replace(/"/g, '\\"') + '"]'
        );
        if (stillThere) stillThere.querySelectorAll('[data-gg-qty]').forEach(function (b) { b.disabled = false; });
      });
      return;
    }

    var rm = t.closest('[data-gg-remove]');
    if (rm) {
      var l = rm.closest('[data-gg-line]');
      if (!l || l.classList.contains('gg-cart-line--removing')) return;
      /* Fade + collapse the row in place first, THEN fire the removal —
         so it reads as a deliberate, soft dismissal instead of the row
         just vanishing the instant the network call resolves. */
      l.classList.add('gg-cart-line--removing');
      setTimeout(function () {
        change(l.getAttribute('data-key'), 0);
      }, 200);
      return;
    }

    var sug = t.closest('[data-gg-add-suggestion]');
    if (sug && !sug.disabled) {
      sug.disabled = true;
      var original = sug.textContent;
      sug.textContent = '…';
      add(sug.getAttribute('data-gg-add-suggestion'), 1).finally(function () {
        /* If the button node survived (no re-render), restore it; otherwise
           the fresh render already replaced it with an enabled copy. */
        if (document.body.contains(sug)) {
          sug.disabled = false;
          sug.textContent = original;
        }
      });
    }
  }, true);

  document.addEventListener('change', function (e) {
    var t = e.target;
    if (t && t.matches && t.matches('textarea[name="note"]') && root() && root().contains(t)) {
      post('cart/update.js', { note: t.value }).catch(function () { /* non-critical */ });
    }
  });

  /* Catches every plain add-to-cart <form> anywhere on the site (product
     cards, PDP, kit builder, etc.) and opens the drawer instead of letting
     it fall through to a full-page submit / navigation. */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.action.indexOf('/cart/add') === -1) return;
    e.preventDefault();
    var submitBtn = form.querySelector('[type="submit"]');
    if (submitBtn) submitBtn.disabled = true;
    addForm(new FormData(form))
      .then(function () { open(true); })
      .finally(function () {
        if (submitBtn) submitBtn.disabled = false;
      });
  });

  document.addEventListener('keydown', function (e) {
    if (!isOpen()) return;
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    if (e.key !== 'Tab') return;
    var p = panel();
    if (!p) return;
    var f = Array.prototype.filter.call(
      p.querySelectorAll('a[href],button:not([disabled]),textarea,summary,input:not([disabled])'),
      function (n) { return n.offsetParent !== null; }
    );
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1], active = document.activeElement;
    if (!p.contains(active)) { e.preventDefault(); first.focus(); }
    else if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
  });

  document.addEventListener('gg:cart:added', function () {
    refresh().then(function () { open(true); });
  });
  document.addEventListener('gg:cart:refresh', function () { refresh(); });

  document.addEventListener('shopify:section:load', function (e) {
    if (e.detail && e.detail.sectionId === sectionId()) updateCount();
  });

  window.addEventListener('pageshow', function (e) {
    if (e.persisted) {
      var r = root();
      if (r) { r.classList.remove('is-open'); r.hidden = true; }
      document.documentElement.style.overflow = '';
      refresh();
    }
  });

  window.ggCart = { open: open, close: close, refresh: refresh, add: add, addForm: addForm, addItems: addItems, change: change };
})();